import { Routes } from '@angular/router';
import { AuthGuard } from './core/auth/auth.guard';
import { CHAT_UI_ENABLED } from './shared/feature-flags';
import { environment } from '../environments/environment';
import { SKIPPER_SCREEN_KEY, SkipperScreenId } from './features/skipper/engine/skipper-screen.service';

const loadHome = () =>
  import('./components/home-page/home-page.component').then((m) => m.HomePageComponent);

/**
 * Screens the profile panel puts in the address bar itself, via
 * `location.replaceState('/watch')`, `/profile/dockmates/find`, `/fleets/<id>`
 * and friends. They are not separate pages — Home hosts the panel, and
 * `UserProfileComponent` already reads `window.location.pathname` on init to
 * restore the tab — but the router still has to KNOW them.
 *
 * It didn't. Every one of these URLs fell through to the `**` wildcard, which
 * redirected to the login page; the login page then bounced an already-signed-in
 * user to `/home`. So refreshing anywhere inside the profile threw you back to
 * the map having lost your place, which is the "refreshing logged me out and
 * started me over" report. Registering them behind AuthGuard makes refresh land
 * where you were, and — if the session really has expired — sends you through
 * login with a returnUrl that brings you back to the same screen.
 */
const PROFILE_PANEL_PATHS = [
  'profile',
  'watch',
  'dockmates',
  'fleets',
  'trips',
  'waypoints',
  'photos',
  'posts',
  'vessels',
  'settings',
];

/**
 * Which screen Skipper treats each route as (greeting, help topics). Dockmates
 * and fleets are the web's Community; the other panel tabs are Profile.
 */
const skipperScreen = (id: SkipperScreenId) => ({ [SKIPPER_SCREEN_KEY]: id });
const panelScreen = (tab: string) =>
  skipperScreen(tab === 'dockmates' || tab === 'fleets' ? 'community_screen' : 'profile_screen');

const profilePanelRoutes: Routes = PROFILE_PANEL_PATHS.flatMap((tab) => [
  { path: tab, canActivate: [AuthGuard], loadComponent: loadHome, data: panelScreen(tab) },
  // Sub-tab (`/dockmates/find`) and sub-tab + id (`/dockmates/find/<userId>`).
  { path: `${tab}/:subTab`, canActivate: [AuthGuard], loadComponent: loadHome, data: panelScreen(tab) },
  { path: `${tab}/:subTab/:id`, canActivate: [AuthGuard], loadComponent: loadHome, data: panelScreen(tab) },
]);

export const routes: Routes = [
  {
    path: 'auth/login',
    loadComponent: () =>
      import('./components/login-page/login-page.component').then(
        (m) => m.LoginPageComponent
      ),
  },
  {
    path: 'auth/create-profile',
    loadComponent: () =>
      import('./components/create-profile/create-profile.component').then(
        (m) => m.CreateProfileComponent
      ),
  },
  {
    path: 'auth/create-vessel',
    loadComponent: () =>
      import('./components/create-vessel/create-vessel.component').then(
        (m) => m.CreateVesselComponent
      ),
  },
  {
    path: 'home',
    canActivate: [AuthGuard],
    data: skipperScreen('home_screen'),
    loadComponent: () =>
      import('./components/home-page/home-page.component').then(
        (m) => m.HomePageComponent
      ),
  },
  {
    path: 'trip/:id',
    canActivate: [AuthGuard],
    data: skipperScreen('home_screen'),
    loadComponent: () =>
      import('./components/home-page/home-page.component').then(
        (m) => m.HomePageComponent
      ),
  },
  {
    path: 'waypoint/:id',
    canActivate: [AuthGuard],
    data: skipperScreen('home_screen'),
    loadComponent: () =>
      import('./components/home-page/home-page.component').then(
        (m) => m.HomePageComponent
      ),
  },
  {
    path: 'trips/:id',
    canActivate: [AuthGuard],
    data: skipperScreen('home_screen'),
    loadComponent: () =>
      import('./components/home-page/home-page.component').then(
        (m) => m.HomePageComponent
      ),
  },
  {
    path: 'waypoints/:id',
    canActivate: [AuthGuard],
    data: skipperScreen('home_screen'),
    loadComponent: () =>
      import('./components/home-page/home-page.component').then(
        (m) => m.HomePageComponent
      ),
  },
  {
    path: 'dockmates/:id/full-profile',
    canActivate: [AuthGuard],
    data: skipperScreen('community_screen'),
    loadComponent: () =>
      import('./components/dockmate-full-profile-page/dockmate-full-profile-page.component').then(
        (m) => m.DockmateFullProfilePageComponent
      ),
  },
  // While chat is hidden the route stays registered but renders nothing — a
  // direct /chat URL lands on Home instead of the unfinished screen.
  CHAT_UI_ENABLED
    ? {
        path: 'chat',
        canActivate: [AuthGuard],
        data: skipperScreen('chat_list_screen'),
        loadComponent: () =>
          import('./chat-v1/messages/messages.component').then(
            (m) => m.MessagesComponent
          ),
      }
    : { path: 'chat', redirectTo: 'home', pathMatch: 'full' as const },
  ...profilePanelRoutes,
  // Dev-only Skipper rig inspector; never registered in production builds.
  ...(environment.production
    ? []
    : [
        {
          path: 'dev/skipper',
          loadComponent: () =>
            import('./features/skipper/presentation/skipper-dev-demo/skipper-dev-demo.component').then(
              (m) => m.SkipperDevDemoComponent
            ),
        },
      ]),
  // Land on Home, not the login form. AuthGuard is what decides whether the
  // session is good; sending everyone to /auth/login first meant a signed-in
  // user bounced login → home and lost the URL they actually asked for.
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'home',
  },
];
